import {
  Box,
  Button,
  Flex,
  IconButton,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { FaPlus, FaRegCalendarAlt, FaTrash } from "react-icons/fa";
import { CrmTask, TaskStatus } from "../../../models/Task";
import { deleteTask, getTaskStatuses, getTasks } from "../../../services/taskService";
import { getUsers } from "../../../services/userService";
import { crm, Pill } from "./crmTheme";
import TaskFormModal from "./TaskFormModal";

interface UserOption {
  id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}
const userLabel = (u: UserOption) =>
  `${u.firstname ?? ""} ${u.lastname ?? ""}`.trim() || u.email || "Usuário";

// Lista de tasks atribuídas a um prospect, exibida ao lado do formulário do lead.
// Admin pode filtrar por usuário; não-admin só vê as próprias (regra aplicada no backend).
const ProspectTasksPanel = ({
  prospectId,
  isAdmin,
}: {
  prospectId: string;
  isAdmin: boolean;
}) => {
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [statuses, setStatuses] = useState<TaskStatus[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [userFilter, setUserFilter] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | undefined>();

  useEffect(() => {
    getTaskStatuses().then(setStatuses);
    if (isAdmin) getUsers().then((d) => setUsers(d ?? []));
  }, [isAdmin]);

  const load = useCallback(async () => {
    const data = await getTasks({
      prospectId,
      userId: isAdmin ? userFilter || undefined : undefined,
    });
    setTasks(data);
  }, [prospectId, isAdmin, userFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id?: string) => {
    if (!id) return;
    await deleteTask(id);
    load();
  };

  return (
    <Box
      w="320px"
      flexShrink={0}
      borderLeft={`1px solid ${crm.border}`}
      pl="20px"
      ml="20px"
      h="100%"
      overflowY="auto"
    >
      <Flex justify="space-between" align="center" mb="12px">
        <Text fontWeight="700" fontSize="sm" color={crm.text}>
          Tasks da empresa
        </Text>
        <Button
          size="xs"
          leftIcon={<FaPlus />}
          bg={crm.brand}
          color="white"
          _hover={{ bg: crm.brandDark }}
          borderRadius="8px"
          onClick={() => {
            setEditingTaskId(undefined);
            setFormOpen(true);
          }}
        >
          Nova
        </Button>
      </Flex>

      {isAdmin && (
        <Select
          size="sm"
          mb="12px"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          borderColor={crm.border}
          borderRadius="8px"
          fontSize="sm"
        >
          <option value="">Todos os usuários</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {userLabel(u)}
            </option>
          ))}
        </Select>
      )}

      <VStack align="stretch" spacing="8px">
        {tasks.map((t) => {
          const overdue = t.dueDate && new Date(t.dueDate).getTime() < Date.now();
          return (
            <Box
              key={t.id}
              p="10px"
              borderRadius="10px"
              bg={crm.surface}
              border={`1px solid ${crm.border}`}
              cursor="pointer"
              _hover={{ borderColor: crm.brand, boxShadow: crm.shadowSm }}
              transition="all .1s"
              onClick={() => {
                setEditingTaskId(t.id);
                setFormOpen(true);
              }}
            >
              <Flex justify="space-between" align="flex-start" gap="6px">
                <Text fontWeight="600" fontSize="sm" noOfLines={2} color={crm.text}>
                  {t.title}
                </Text>
                <IconButton
                  aria-label="Excluir task"
                  icon={<FaTrash />}
                  size="xs"
                  variant="ghost"
                  color={crm.textMuted}
                  _hover={{ color: crm.danger }}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(t.id);
                  }}
                />
              </Flex>
              <Flex mt="6px" gap="5px" flexWrap="wrap">
                {t.taskStatusName && (
                  <Pill>{t.taskStatusName}</Pill>
                )}
                {t.dueDate && (
                  <Pill
                    bg={overdue ? "#fef2f2" : crm.brandSoft}
                    color={overdue ? crm.danger : crm.brand}
                  >
                    <FaRegCalendarAlt size={9} />
                    {new Date(t.dueDate).toLocaleDateString("pt-BR")}
                  </Pill>
                )}
              </Flex>
            </Box>
          );
        })}
        {tasks.length === 0 && (
          <Text fontSize="sm" color={crm.textFaint} textAlign="center" py="20px">
            Nenhuma task para esta empresa.
          </Text>
        )}
      </VStack>

      <TaskFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={load}
        statuses={statuses}
        isAdmin={isAdmin}
        taskId={editingTaskId}
        defaultProspectId={prospectId}
        lockProspect
      />
    </Box>
  );
};

export default ProspectTasksPanel;
